import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Layout, Row, Col, Breadcrumb, Table, Button, Input, Select, DatePicker,
    Tag, Typography, Space, Segmented, Empty, Spin, Tooltip, Drawer, Timeline,
    Descriptions, Alert, Divider, message
} from "antd";
import {
    HomeOutlined, HistoryOutlined, SearchOutlined, FilterOutlined, ClearOutlined,
    ReloadOutlined, FilePdfOutlined, FileExcelOutlined, ProfileOutlined,
    EyeOutlined, ExperimentOutlined, LinkOutlined, ClockCircleOutlined
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../services/axiosInstance";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildReportPDF } from "../../../utils/reportPdfBuilder";
import { exportRowsToExcel } from "../../../utils/reportExport";
import {
    actionMeta, actionDotColor, formatLogDateTime as formatDateTime,
    timeAgo, logProductLabel as productLabel,
} from "../../../utils/productionLogMeta";
import dayjs from "dayjs";
import { isPermissionAllowed } from "../../../utils/permissionHelper";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

// Matches the server-side ceiling in BACKEND/api/production/log_list.php
const EXPORT_LIMIT = 2000;

const BATCH_STATUS_COLORS = {
    Pending: "orange",
    Ongoing: "blue",
    Finished: "green",
};

const ProductionLogs = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // The Production Batch detail page links here with ?prodb=<id> so the
    // history of a single batch can be reviewed straight from the batch.
    const batchFilter = Number(searchParams.get("prodb") || 0);

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [stats, setStats] = useState({ total_entries: 0, batches_touched: 0, latest_change: null });
    const [options, setOptions] = useState({ actions: [], users: [], products: [] });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    // Filter state (draft + applied)
    const [search, setSearch] = useState("");
    const [filterAction, setFilterAction] = useState(undefined);
    const [filterUser, setFilterUser] = useState(undefined);
    const [filterProduct, setFilterProduct] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);
    const [applied, setApplied] = useState({});

    const [viewMode, setViewMode] = useState("table");
    const [detailLog, setDetailLog] = useState(null);

    // Export state
    const [exporting, setExporting] = useState(false);
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [includeFilters, setIncludeFilters] = useState(false);

    const buildFilterPayload = useCallback((filters = {}) => ({
        search: filters.search || "",
        action: filters.action || "",
        ch_by: filters.ch_by || 0,
        sp_id: filters.sp_id || 0,
        prodb_id: filters.prodb_id || 0,
        date_from: filters.date_from || "",
        date_to: filters.date_to || "",
    }), []);

    const fetchLogs = useCallback(async (page = 1, pageSize = 20, filters = {}) => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.post("/production/log_list.php", {
                page,
                limit: pageSize,
                ...buildFilterPayload(filters),
            });
            if (res.data.status !== "success") {
                throw new Error(res.data.message || "Failed to load production logs.");
            }
            setLogs(res.data.dataset || []);
            setStats(res.data.stats || { total_entries: 0, batches_touched: 0, latest_change: null });
            if (res.data.filter_options) {
                setOptions({
                    actions: res.data.filter_options.actions || [],
                    users: res.data.filter_options.users || [],
                    products: res.data.filter_options.products || [],
                });
            }
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 20,
                total: res.data.pagination?.total_records || 0,
            });
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to load production logs.";
            setLogs([]);
            setLoadError(msg);
        } finally {
            setLoading(false);
        }
    }, [buildFilterPayload]);

    useEffect(() => {
        const initial = batchFilter > 0 ? { prodb_id: batchFilter } : {};
        setApplied(initial);
        fetchLogs(1, 20, initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchFilter, fetchLogs]);

    const appliedProductLabel = useMemo(() => {
        if (!applied.sp_id) return "-";
        const found = options.products.find((p) => Number(p.value) === Number(applied.sp_id));
        return found ? found.label : `#${applied.sp_id}`;
    }, [applied, options.products]);

    const appliedUserLabel = useMemo(() => {
        if (!applied.ch_by) return "-";
        const found = options.users.find((u) => Number(u.value) === Number(applied.ch_by));
        return found ? found.label : `#${applied.ch_by}`;
    }, [applied, options.users]);

    // Batch ref for the "single batch" banner (?prodb=<id>)
    const batchScopeLabel = useMemo(() => {
        if (!batchFilter) return "";
        const found = logs.find((l) => Number(l.prodb_id) === Number(batchFilter));
        return found?.prodb_ref || `#${batchFilter}`;
    }, [batchFilter, logs]);

    const appliedActionLabel = useMemo(() => {
        if (!applied.action) return "-";
        return actionMeta(applied.action).label;
    }, [applied.action]);

    const hasAppliedFilters = Boolean(
        applied.search || applied.action || applied.ch_by ||
        applied.sp_id || applied.date_from || applied.date_to
    );

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            action: filterAction || "",
            ch_by: filterUser || 0,
            sp_id: filterProduct || 0,
            date_from: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
            date_to: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
        };
        setApplied(filters);
        fetchLogs(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch("");
        setFilterAction(undefined);
        setFilterUser(undefined);
        setFilterProduct(undefined);
        setDateRange(null);
        setApplied({});

        // Dropping the ?prodb=<id> scope re-runs the effect (which refetches),
        // so only fetch here when there was nothing to clear.
        if (batchFilter > 0) {
            setSearchParams({}, { replace: true });
        } else {
            fetchLogs(1, pagination.pageSize, {});
        }
    };

    const refresh = () => fetchLogs(pagination.current, pagination.pageSize, applied);

    /** Jump to the ?prodb=<id> view showing only one batch's history. */
    const showBatchOnly = (prodbId) => {
        if (!prodbId) return;
        setDetailLog(null);
        setViewMode("table");
        setSearch("");
        setFilterAction(undefined);
        setFilterUser(undefined);
        setFilterProduct(undefined);
        setDateRange(null);

        const next = { prodb_id: prodbId };
        setApplied(next);

        // Changing the query string re-runs the loading effect; when the scope
        // is already the same batch there is nothing to change, so fetch here.
        if (Number(searchParams.get("prodb") || 0) === Number(prodbId)) {
            fetchLogs(1, pagination.pageSize, next);
        } else {
            setSearchParams({ prodb: String(prodbId) }, { replace: true });
        }
    };

    /* ─── Export ────────────────────────────────────────────── */

    const buildExportPayload = () => ({
        page: 1,
        limit: EXPORT_LIMIT,
        ...buildFilterPayload(applied),
    });

    const fetchRowsForExport = async () => {
        const res = await api.post("/production/log_list.php", buildExportPayload());
        const rows = res.data.dataset || [];
        if (rows.length >= EXPORT_LIMIT) {
            message.warning(`Only the first ${EXPORT_LIMIT} matching entries were exported. Narrow the filters to export the rest.`);
        }
        return rows;
    };

    const EXPORT_COLUMNS = [
        { title: "Date & Time", dataIndex: "ch_dt_fmt", width: 22 },
        { title: "Action", dataIndex: "action_label", width: 24 },
        { title: "Batch Ref", dataIndex: "prodb_ref", width: 16 },
        { title: "Product", dataIndex: "product", width: 30 },
        { title: "Details", dataIndex: "log_message", width: 60 },
        { title: "Changed By", dataIndex: "changed_by_name", width: 20 },
        { title: "Batch Status", dataIndex: "batch_status", width: 15 },
    ];

    const buildExportRows = (rows) => rows.map((r) => ({
        ch_dt_fmt: formatDateTime(r.ch_dt),
        action_label: actionMeta(r.log_action).label,
        prodb_ref: r.prodb_ref || "-",
        product: productLabel(r),
        log_message: r.log_message || "-",
        changed_by_name: r.changed_by_name || "-",
        batch_status: r.batch_deleted ? "Deleted" : (r.batch_status || "-"),
    }));

    const buildFiltersSummary = () => {
        const summary = {};
        if (applied.search) summary["Search"] = applied.search;
        if (applied.action) summary["Action"] = appliedActionLabel;
        if (applied.sp_id) summary["Product"] = appliedProductLabel;
        if (applied.ch_by) summary["Changed By"] = appliedUserLabel;
        if (applied.date_from || applied.date_to) {
            summary["Date Range"] = `${applied.date_from || "start"} to ${applied.date_to || "today"}`;
        }
        return summary;
    };

    const buildExportDoc = async (rows, withFilters) => buildReportPDF({
        title: "PRODUCTION LOGS REPORT",
        subtitle: "Production batch activity history",
        columns: EXPORT_COLUMNS.map((c) => ({ header: c.title })),
        rows: buildExportRows(rows).map((r) => EXPORT_COLUMNS.map((c) => r[c.dataIndex] ?? "")),
        includeFilters: withFilters,
        filters: buildFiltersSummary(),
        orientation: "landscape",
    });

    const handleExportPDF = async () => {
        setExporting(true);
        try {
            const rows = await fetchRowsForExport();
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildExportDoc(rows, includeFilters);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF export failed:", err);
            message.error(err?.response?.data?.message || "Failed to export PDF.");
        } finally {
            setPdfLoading(false);
            setExporting(false);
        }
    };

    const handleIncludeFiltersChange = async (checked) => {
        setIncludeFilters(checked);
        setPdfLoading(true);
        setPdfDataUri(null);
        try {
            const rows = await fetchRowsForExport();
            const doc = await buildExportDoc(rows, checked);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        if (!pdfDataUri) return;
        const link = document.createElement("a");
        link.href = pdfDataUri;
        link.download = `production-logs-${dayjs().format("YYYYMMDD-HHmm")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const rows = await fetchRowsForExport();
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }
            exportRowsToExcel({
                filename: "production-logs",
                sheetName: "Production Logs",
                columns: EXPORT_COLUMNS,
                rows: buildExportRows(rows),
            });
            message.success("Excel file exported.");
        } catch (err) {
            console.error("Excel export failed:", err);
            message.error(err?.response?.data?.message || "Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    /* ─── Renderers ─────────────────────────────────────────── */

    const renderActionTag = (action) => {
        const meta = actionMeta(action);
        return (
            <Tag color={meta.color} icon={meta.icon} style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {meta.label}
            </Tag>
        );
    };

    const renderBatch = (record) => (
        <Space direction="vertical" size={2}>
            <Typography.Link
                onClick={() => record.prodb_id && navigate(`/production/production-batches/${record.prodb_id}`)}
                strong
            >
                {record.prodb_ref || `#${record.prodb_id}`}
            </Typography.Link>
            {record.batch_deleted ? (
                <Tag color="red" style={{ marginInlineEnd: 0 }}>Deleted batch</Tag>
            ) : (
                <Tag color={BATCH_STATUS_COLORS[record.batch_status] || "default"} style={{ marginInlineEnd: 0 }}>
                    {record.batch_status || "-"}
                </Tag>
            )}
        </Space>
    );

    const columns = [
        {
            title: "Date & Time",
            dataIndex: "ch_dt",
            key: "ch_dt",
            width: 190,
            render: (v) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text strong style={{ fontSize: 13 }}>{formatDateTime(v)}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{timeAgo(v)}</Typography.Text>
                </Space>
            ),
        },
        {
            title: "Action",
            dataIndex: "log_action",
            key: "log_action",
            width: 210,
            filters: options.actions.map((a) => ({ text: a.label, value: a.value })),
            filterMultiple: false,
            filteredValue: applied.action ? [applied.action] : null,
            render: (action) => renderActionTag(action),
        },
        {
            title: "Batch",
            key: "batch",
            width: 170,
            render: (_, record) => renderBatch(record),
        },
        {
            title: "Product",
            key: "product",
            width: 230,
            render: (_, record) => productLabel(record),
        },
        {
            title: "What Happened",
            dataIndex: "log_message",
            key: "log_message",
            render: (v) => (
                <Tooltip title={v}>
                    <Typography.Text style={{ fontSize: 13 }}>{v || "-"}</Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: "Changed By",
            dataIndex: "changed_by_name",
            key: "changed_by_name",
            width: 170,
            render: (v) => v || "-",
        },
        {
            title: "Details",
            key: "details",
            align: "center",
            width: 90,
            fixed: "right",
            render: (_, record) => (
                <Tooltip title="View full log entry">
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => { e.stopPropagation(); setDetailLog(record); }}
                    />
                </Tooltip>
            ),
        },
    ];

    const renderStats = () => (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={8}>
                <div className="fluid-status-card" style={{ background: "rgba(22, 119, 255, 0.08)", border: "1px solid rgba(22, 119, 255, 0.2)" }}>
                    <div className="fluid-stat-value">{Number(stats.total_entries || 0).toLocaleString()}</div>
                    <div className="fluid-stat-label">
                        <HistoryOutlined /> Log entries {hasAppliedFilters ? "(matching filters)" : ""}
                    </div>
                </div>
            </Col>
            <Col xs={24} sm={12} lg={8}>
                <div className="fluid-status-card" style={{ background: "rgba(82, 196, 26, 0.08)", border: "1px solid rgba(82, 196, 26, 0.2)" }}>
                    <div className="fluid-stat-value">{Number(stats.batches_touched || 0).toLocaleString()}</div>
                    <div className="fluid-stat-label">
                        <ExperimentOutlined /> Batches touched
                    </div>
                </div>
            </Col>
            <Col xs={24} sm={24} lg={8}>
                <div className="fluid-status-card" style={{ background: "rgba(250, 173, 20, 0.08)", border: "1px solid rgba(250, 173, 20, 0.2)" }}>
                    <div className="fluid-stat-value" style={{ fontSize: 20 }}>
                        {stats.latest_change ? formatDateTime(stats.latest_change) : "-"}
                    </div>
                    <div className="fluid-stat-label">
                        <ClockCircleOutlined /> Latest activity
                        {stats.latest_change ? ` \u00b7 ${timeAgo(stats.latest_change)}` : ""}
                    </div>
                </div>
            </Col>
        </Row>
    );

    const renderTimeline = () => (
        <Timeline
            style={{ marginTop: 8 }}
            items={logs.map((record) => {
                const meta = actionMeta(record.log_action);
                return {
                    dot: React.cloneElement(meta.icon, { style: { color: actionDotColor(record.log_action) } }),
                    children: (
                        <div style={{ paddingBottom: 4 }}>
                            <Space wrap size={8}>
                                {renderActionTag(record.log_action)}
                                <Typography.Text strong>{record.prodb_ref || `#${record.prodb_id}`}</Typography.Text>
                                <Tag color={record.batch_deleted ? "red" : (BATCH_STATUS_COLORS[record.batch_status] || "default")}>
                                    {record.batch_deleted ? "Deleted batch" : (record.batch_status || "-")}
                                </Tag>
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {formatDateTime(record.ch_dt)}{" \u00b7 "}{timeAgo(record.ch_dt)}
                                </Typography.Text>
                            </Space>
                            <div style={{ marginTop: 4 }}>
                                <Typography.Text>{record.log_message || "-"}</Typography.Text>
                            </div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {productLabel(record)}{" \u00b7 by "}<strong>{record.changed_by_name || "-"}</strong>
                            </Typography.Text>
                        </div>
                    ),
                };
            })}
        />
    );

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Production' },
                                { title: 'Production Logs' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <HistoryOutlined /> Production Logs
                        </h1>
                        <p className="fluid-page-subtitle">
                            Every creation, update, start, finish and production process event recorded against a batch
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading} className="hero-action-btn">
                                Refresh
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                {renderStats()}

                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={[16, 12]} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Ref, product, detail, user"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Action:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterAction}
                                    onChange={setFilterAction}
                                    placeholder="All Actions"
                                    allowClear
                                >
                                    {options.actions.map((a) => <Option key={a.value} value={a.value}>{a.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Product:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterProduct}
                                    onChange={setFilterProduct}
                                    placeholder="All Products"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {options.products.map((p) => <Option key={p.value} value={Number(p.value)}>{p.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Changed By:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterUser}
                                    onChange={setFilterUser}
                                    placeholder="All Users"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {options.users.map((u) => <Option key={u.value} value={Number(u.value)}>{u.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={24} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Changed Between:</div>
                                <DatePicker.RangePicker
                                    style={{ width: "100%" }}
                                    value={dateRange}
                                    onChange={setDateRange}
                                    allowEmpty={[true, true]}
                                    placeholder={["From", "To"]}
                                />
                            </Col>
                            <Col xs={24} sm={24} lg={5}>
                                {/* Filter and export actions share the same line as the
                                    filter fields, so the bar is always a single row. */}
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                    {isPermissionAllowed('prod_logs_export') && (
                                        <>
                                            <Tooltip title="Export to PDF (respects current filters)">
                                                <Button
                                                    icon={<FilePdfOutlined style={{ color: "#ff4d4f" }} />}
                                                    onClick={handleExportPDF}
                                                    loading={exporting}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Export to Excel (respects current filters)">
                                                <Button
                                                    icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                                    onClick={handleExportExcel}
                                                    loading={exporting}
                                                />
                                            </Tooltip>
                                        </>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
                            <Col flex="auto">
                                {hasAppliedFilters && (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        Showing results for — Search: {applied.search || "-"} · Action: {appliedActionLabel} ·
                                        {" "}Product: {appliedProductLabel} · Changed By: {appliedUserLabel} · Dates: {(applied.date_from || "start") + " to " + (applied.date_to || "today")}
                                    </Typography.Text>
                                )}
                            </Col>
                            <Col>
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { label: "Table", value: "table" },
                                        { label: "Timeline", value: "timeline" },
                                    ]}
                                />
                            </Col>
                        </Row>
                        {batchFilter > 0 && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginTop: 10 }}
                                message={`Showing the full activity history of batch ${batchScopeLabel} only`}
                                action={<Button size="small" onClick={handleClear}>Show all logs</Button>}
                            />
                        )}
                    </div>

                    {loadError ? (
                        <Alert
                            type="error"
                            showIcon
                            message="Could not load production logs"
                            description={loadError}
                            action={<Button size="small" icon={<ReloadOutlined />} onClick={refresh}>Retry</Button>}
                            style={{ margin: "0 0 16px" }}
                        />
                    ) : null}

                    <Spin spinning={loading}>
                        {!loadError && logs.length === 0 && !loading ? (
                            <Empty
                                description="No production log entries found"
                                style={{ padding: "40px 0" }}
                            />
                        ) : !loadError && viewMode === "table" ? (
                            <div className="fluid-table">
                                <Table
                                    size="small"
                                    columns={columns}
                                    dataSource={logs}
                                    rowKey="prodlog_id"
                                    onRow={(record) => ({
                                        onClick: () => setDetailLog(record),
                                        style: { cursor: "pointer" },
                                    })}
                                    onChange={(pg, filters) => {
                                        const nextAction = filters?.log_action?.[0] || "";
                                        if (nextAction !== (applied.action || "")) {
                                            setFilterAction(nextAction || undefined);
                                            const nextFilters = { ...applied, action: nextAction };
                                            setApplied(nextFilters);
                                            fetchLogs(1, pg.pageSize || pagination.pageSize, nextFilters);
                                            return;
                                        }
                                        fetchLogs(pg.current, pg.pageSize, applied);
                                    }}
                                    pagination={{
                                        current: pagination.current,
                                        pageSize: pagination.pageSize,
                                        total: pagination.total,
                                        showSizeChanger: true,
                                        pageSizeOptions: ["10", "20", "50", "100"],
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
                                    }}
                                    loading={loading}
                                    scroll={{ x: 1200 }}
                                />
                            </div>
                        ) : !loadError ? (
                            <div style={{ padding: "8px 4px" }}>
                                {renderTimeline()}
                                <div style={{ textAlign: "right", marginTop: 12 }}>
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        Showing {logs.length} of {pagination.total} entries — use the table view to page through all entries.
                                    </Typography.Text>
                                </div>
                            </div>
                        ) : null}
                    </Spin>
                </div>
            </Content>

            <Footerdiv />

            {/* Full log entry */}
            <Drawer
                title="Log Entry Details"
                placement="right"
                width={520}
                open={!!detailLog}
                onClose={() => setDetailLog(null)}
            >
                {detailLog && (
                    <>
                        <Space wrap style={{ marginBottom: 12 }}>
                            {renderActionTag(detailLog.log_action)}
                            <Tag color={detailLog.batch_deleted ? "red" : (BATCH_STATUS_COLORS[detailLog.batch_status] || "default")}>
                                {detailLog.batch_deleted ? "Deleted batch" : (detailLog.batch_status || "-")}
                            </Tag>
                        </Space>
                        <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label="Log ID">#{detailLog.prodlog_id}</Descriptions.Item>
                            <Descriptions.Item label="Batch">
                                {detailLog.prodb_ref || `#${detailLog.prodb_id}`}
                            </Descriptions.Item>
                            <Descriptions.Item label="Product">{productLabel(detailLog)}</Descriptions.Item>
                            <Descriptions.Item label="Action">{actionMeta(detailLog.log_action).label}</Descriptions.Item>
                            <Descriptions.Item label="What Happened">
                                {detailLog.log_message || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Full Record">{detailLog.log_detail || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Changed By">{detailLog.changed_by_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Changed At">
                                {formatDateTime(detailLog.ch_dt)}{" ("}{timeAgo(detailLog.ch_dt)}{")"}
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider />
                        <Button
                            type="primary"
                            icon={<LinkOutlined />}
                            disabled={!detailLog.prodb_id}
                            onClick={() => {
                                setDetailLog(null);
                                navigate(`/production/production-batches/${detailLog.prodb_id}`);
                            }}
                        >
                            Open Batch
                        </Button>
                        <Button
                            icon={<ProfileOutlined />}
                            style={{ marginLeft: 8 }}
                            disabled={!detailLog.prodb_id}
                            onClick={() => showBatchOnly(detailLog.prodb_id)}
                        >
                            Show this batch only
                        </Button>
                    </>
                )}
            </Drawer>

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title="Production Logs Report"
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                includeFilters={includeFilters}
                onIncludeFiltersChange={handleIncludeFiltersChange}
                onDownload={handleDownloadPDF}
            />
        </Layout>
    );
};

export default ProductionLogs;
